import React, { createRef, useState, useEffect, useMemo, useCallback, useRef } from 'react';

import GridItem from './GridItem/GridItem';
import Tags from './Tags';
import { Button } from 'primereact/button';
import './GridView.scss';

import { useFileManagerContext } from 'modules/FileManagerV2/context/FileManagerContext';
import useFileDownload from 'modules/FileManagerV2/hooks/ui/useFileDownload';
import useUpdateFiles from 'modules/FileManagerV2/hooks/api/useUpdateFiles';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';

function GridView() {
  const {
    files,
    modals,
    selectedRows,
    isGlobalFileManager,
    isClientFileManager,
    handleGetFiles,
    setSelectedRows,
    paginationToken,
    onNextPageClick,
  } = useFileManagerContext();

  const { setShowBulkDeleteModal, setShowBulkMoveModal, setShowShareFileModal, setIsCopy } = modals;
  const { downloadFile } = useFileDownload();
  const { fileBulkMarkAsRead } = useUpdateFiles();
  const { addToast } = useToast();
  const [isLazyLoading, setIsLazyLoading] = useState(false);
  const [accumulatedFiles, setAccumulatedFiles] = useState([]);
  // True while we are waiting for the next-page API response to arrive
  const loadingNextPageRef = useRef(false);
  // Sentinel element observed by IntersectionObserver to detect true bottom
  const sentinelRef = useRef(null);

  // One ref per file item — used by MenuIcon for popup menu toggling
  const itemsRef = useMemo(
    () =>
      Array(accumulatedFiles?.length ?? 0)
        .fill(null)
        .map(() => createRef()),
    [accumulatedFiles]
  );

  const containsFolders = useMemo(() => selectedRows.some((item) => item.entry_type === 'folder'), [selectedRows]);

  const downloadFiles = useCallback(async () => {
    try {
      await Promise.all(
        selectedRows.map(async (file) => {
          await downloadFile(file, () => handleGetFiles());
        })
      );
      console.log('All files downloaded successfully');
      setSelectedRows([]);
    } catch (error) {
      console.error('Error downloading files:', error);
    }
  }, [selectedRows, downloadFile, handleGetFiles, setSelectedRows]);

  const markFilesAsRead = useCallback(async () => {
    try {
      const fileItems = selectedRows?.map((file) => ({ pk: file?.pk, sk: file?.sk }));
      await fileBulkMarkAsRead({ file_items: fileItems, is_read: true });
      addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, 'Files marked as read successfully');
      handleGetFiles();
      setSelectedRows([]);
    } catch (error) {
      console.error('Error marking files as read:', error);
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, 'Failed to mark files as read');
    }
  }, [selectedRows, fileBulkMarkAsRead, handleGetFiles, setSelectedRows]);

  useEffect(() => {
    if (loadingNextPageRef.current && (!files || files.length === 0)) return;

    if (loadingNextPageRef.current) {
      setAccumulatedFiles((prev) => [...prev, ...files]);
      loadingNextPageRef.current = false;
    } else {
      setAccumulatedFiles(files || []);
    }
    setIsLazyLoading(false);
  }, [files]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && paginationToken && !loadingNextPageRef.current) {
          setIsLazyLoading(true);
          loadingNextPageRef.current = true;
          onNextPageClick();
        }
      },
      { threshold: 1.0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [paginationToken, onNextPageClick]);

  return (
    <>
      {selectedRows.length > 0 && (
        <div className="flex flex-wrap align-items-end justify-content-end gap-2 d-flex">
          {!isGlobalFileManager && !isClientFileManager && (
            <>
              <Button className="p-button p-button-primary outline ms-2 bg-" onClick={() => setShowBulkDeleteModal(true)}>
                Delete
              </Button>

              <Button
                className="p-button p-button-primary outline ms-2"
                onClick={() => {
                  setShowBulkMoveModal(true);
                  setIsCopy(false);
                }}
              >
                Move
              </Button>

              <Button
                className="p-button p-button-primary outline ms-2"
                onClick={() => {
                  setShowBulkMoveModal(true);
                  setIsCopy(true);
                }}
              >
                Copy
              </Button>

              <Button className="p-button p-button-primary outline ms-2" onClick={() => setShowShareFileModal(true)}>
                Share
              </Button>
            </>
          )}
          {!containsFolders && (
            <>
              <Button className="p-button p-button-primary outline ms-2" onClick={downloadFiles}>
                {' '}
                Download{' '}
              </Button>
              <Button className="p-button p-button-primary outline ms-2" onClick={markFilesAsRead}>
                {' '}
                Mark as Read{' '}
              </Button>
            </>
          )}
        </div>
      )}

      <div className="grid-view-container">
        {accumulatedFiles.length === 0 && !isLazyLoading ? (
          <p className="grid-view-empty">No data found</p>
        ) : (
          <ul className="grid-view-list">
            {accumulatedFiles.map((item, index) => (
              <li key={item?.sk ?? item?.pk ?? index}>
                <div className="grid-view-cell">
                  <GridItem item={item} />
                  {!isClientFileManager && !isGlobalFileManager && (
                    <Tags item={item} itemsRef={itemsRef} index={index} onDownloadComplete={() => handleGetFiles()} />
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        <div ref={sentinelRef} className="grid-view-sentinel" />
      </div>
    </>
  );
}

export default GridView;
